import React from 'react';
import PropTypes from 'prop-types';
import {
  withDataProvider,
  SimpleForm,
  LongTextInput,
  Create,
  Button,
  SimpleShowLayout,
  Toolbar,
  SaveButton,
  translate as translateHOC,
  showNotification,
  fetchStart,
  fetchEnd,
  CREATE,
} from 'react-admin';
import { isSubmitting as isSubmittingAction, reset, submit } from 'redux-form';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { withStyles, Typography, Grid, Divider } from '@material-ui/core';
import IconCancel from '@material-ui/icons/Cancel';
import { BusinessDetails } from './business-details';
import { BusinessAddress } from './business-address';
import BusinessAdditionalInfo from './business-additional-info';
import { maxLength } from '../../../core/validators';
import { scrollbarStyle } from '../../../helpers/styleHelpers';
import { getApiErrorMessage } from '../../../helpers/apiErrors';

const createStyles = ({ palette, spacing }) => ({
  suggestionsContainerOpen: {
    zIndex: 2100,
  },
  field: {
    float: 'left',
    width: '28%',
    minWidth: '200px',
    marginRight: '5%',
    height: '90px',
  },
  fieldContent: {
    width: '100%',
    marginRight: '0',
  },
  block: {
    width: 'fit-content',
  },
  actions: {
    float: 'right',
  },
  largeButton: {
    float: 'left',
    width: '40%',
    marginRight: '5%',
  },
  fullWidth: {
    width: '100%',
  },
  commentWidth: {
    width: '70%',
  },
  createCard: {
    marginBottom: spacing.unit,
  },
  toolbar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    '& button:first-child': {
      marginRight: spacing.unit / 2,
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${spacing.unit}px`,
    flexWrap: 'nowrap',
    paddingBottom: spacing.unit * 2,
    overflow: 'hidden',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 237px)',
    ...scrollbarStyle(palette),
  },
  requiredMessage: {
    width: 'fit-content',
    paddingTop: spacing.unit * 2,
    paddingBottom: 0,
    paddingLeft: spacing.unit * 3,
  },
  saveToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: `${spacing.unit}px`,
    paddingRight: spacing.unit * 2,
  },
});

const CancelButton = ({ handleCancelClick, className, ...props }) => (
  <Button
    className={className}
    onClick={handleCancelClick}
    label="common.cancel"
    size="medium"
    id="Selenium-Business-Create-Cancel-Button"
    {...props}
  >
    <IconCancel />
  </Button>
);

CancelButton.propTypes = {
  handleCancelClick: PropTypes.func,
  className: PropTypes.string,
};

const BusinessCreateToolbar = withStyles(createStyles)(
  ({ isSubmitting, handleCancelClick, handleSaveClick, classes, invalid, ...props }) => (
    <Toolbar {...props} className={classes.saveToolbar}>
      <CancelButton handleCancelClick={handleCancelClick} variant="outlined" />
      {/* Submits the form itself instead of going through the save of <Create>, so the create
          rejection is handled by the form and not by the notification side effect of react-admin. */}
      <SaveButton
        saving={isSubmitting}
        disabled={invalid}
        handleSubmitWithRedirect={handleSaveClick}
        id="Selenium-Business-Create-Save-Button"
      />
    </Toolbar>
  )
);

BusinessCreateToolbar.propTypes = {
  isSubmitting: PropTypes.bool,
  handleCancelClick: PropTypes.func,
  handleSaveClick: PropTypes.func,
  classes: PropTypes.shape({}),
  invalid: PropTypes.bool,
};

class BusinessCreate extends React.PureComponent {
  useAsBillingAddress = false;

  componentWillMount() {
    const { dispatch } = this.props;
    dispatch(reset('business-create'));
  }

  handleCancelClick = () => {
    const { dispatch } = this.props;
    dispatch(push('/business'));
  };

  // SaveButton invokes this curried, as handleSubmitWithRedirect(redirect)().
  handleSaveClick = () => () => {
    const { dispatch } = this.props;
    dispatch(submit('business-create'));
  };

  // Submitting through the data provider rather than through the default save of <Create> so that a
  // rejection can be reported as a readable line: the notification side effect of react-admin shows
  // the raw error message, which carries the stringified back-end payload.
  handleSubmit = record => {
    const { dispatch, dataProvider } = this.props;
    dispatch(fetchStart());

    return dataProvider(CREATE, 'business', { data: record })
      .then(({ data }) => {
        dispatch(showNotification('ra.notification.created', 'info', { messageArgs: { smart_count: 1 } }));
        dispatch(push(`/business/${data.id}`));
      })
      .catch(err => {
        console.error('[BusinessCreate]', err);
        dispatch(showNotification(getApiErrorMessage(err), 'warning'));
      })
      .finally(() => dispatch(fetchEnd()));
  };

  render() {
    const { dispatch, translate, classes, permissions, isSubmitting, dataProvider, isItaly, ...props } = this.props;
    const { resource } = this.props;
    return (
      <Create classes={{ card: classes.createCard }} title=" " {...props}>
        <SimpleForm
          toolbar={
            <BusinessCreateToolbar
              className={classes.toolbar}
              handleCancelClick={this.handleCancelClick}
              handleSaveClick={this.handleSaveClick}
              classes={classes}
              isSubmitting={isSubmitting}
            />
          }
          form="business-create"
          onSubmit={this.handleSubmit}
        >
          <Grid container className={classes.root} fullWidth>
            <Grid item xs={12}>
              <BusinessDetails resource={resource} classes={classes} translate={translate} />
            </Grid>
            <Divider />
            <Grid item xs={12}>
              <BusinessAddress resource={resource} classes={classes} isItaly={isItaly} />
            </Grid>
            <Divider />
            <Grid item xs={12}>
              <BusinessAdditionalInfo resource={resource} classes={classes} />
            </Grid>
            {isItaly && <Divider />}
            <Grid item xs={12}>
              <SimpleShowLayout resource={resource} className={classes.fullWidth}>
                <LongTextInput
                  source="comment"
                  className={classes.commentWidth}
                  validate={[
                    maxLength(
                      255,
                      translate('validation.maxLength', {
                        max: 255,
                      })
                    ),
                  ]}
                  id="Selenium-Business-Create-Comment-Input"
                />
              </SimpleShowLayout>
            </Grid>
          </Grid>
          <Typography className={classes.requiredMessage}>{translate('validation.fillRequired')}</Typography>
        </SimpleForm>
      </Create>
    );
  }
}

BusinessCreate.propTypes = {
  dataProvider: PropTypes.func,
  isSubmitting: PropTypes.bool,
  dispatch: PropTypes.func,
  translate: PropTypes.func,
  isShowDialog: PropTypes.bool,
  permissions: PropTypes.string,
  resource: PropTypes.string,
  handleCloseDialog: PropTypes.func,
  record: PropTypes.shape({}),
  classes: PropTypes.shape({}),
  isItaly: PropTypes.bool,
};

const mapStateToProps = state => ({
  isSubmitting: isSubmittingAction('business-create')(state),
  isItaly: state.configuration.fleet.timezone === 'Europe/Rome',
});

export default withDataProvider(withStyles(createStyles)(translateHOC(connect(mapStateToProps)(BusinessCreate))));

// For tests
export const BusinessCreateTest = BusinessCreate;
